import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Brain, Sparkles, Target, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { getBackendUrl } from '../../lib/utils';

interface ReviewSkill {
  title: string;
  friendly_reason: string;
}

interface NewSkill {
  title: string;
  success_criteria: string;
}

interface SessionPlan {
  language: string;
  cefr_level: string;
  mode: string;
  today_focus: string;
  stage_labels: string[];
  review_skills: ReviewSkill[];
  new_skill: NewSkill | null;
  repair_note: string | null;
}

interface Props {
  compact?: boolean; // true = LiveSession side panel (stage labels only)
}

export const SessionPlanCard: React.FC<Props> = ({ compact = false }) => {
  const [plan, setPlan] = useState<SessionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPlan() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          setError('Not signed in.');
          return;
        }

        const res = await fetch(
          `${getBackendUrl()}/api/session/plan/current`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );

        if (res.status === 404) {
          // No plan yet — not an error, just no sessions yet
          if (!cancelled) setLoading(false);
          return;
        }
        if (!res.ok) throw new Error(`Server error ${res.status}`);

        const data: SessionPlan = await res.json();
        if (!cancelled) {
          setPlan(data);
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError('Could not load session plan.');
          setLoading(false);
        }
      }
    }

    fetchPlan();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <div className="animate-pulse rounded-3xl bg-panel/10 h-48 mb-6" />
  );

  if (error || !plan) return null; // Fail silently — this is auxiliary UI

  // Compact mode: LiveSession side panel — just the stage pills
  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="pill text-[10.5px] uppercase tracking-wider">{plan.language} {plan.cefr_level}</span>
        {plan.stage_labels.map((label, i) => (
          <span key={i} className="pill border-panel/10 bg-panel/[0.02] text-whisper text-[10.5px]">{label}</span>
        ))}
      </div>
    );
  }

  // Full card: Today.jsx dashboard
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass rounded-3xl p-6 sm:p-8 hairline mb-2"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl border border-aura-cyan/25 bg-aura-cyan/5 grid place-items-center text-aura-cyan shrink-0">
            <Brain size={22} strokeWidth={1.5} />
          </div>
          <div>
            <div className="section-label mb-1">Session Plan</div>
            <h3 className="font-display text-[22px] sm:text-[24px] font-light tracking-tight text-ink leading-tight">
              {plan.today_focus}
            </h3>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span className="pill border-panel/10 bg-panel/[0.02] text-whisper">{plan.cefr_level}</span>
          <span className="pill border-panel/10 bg-panel/[0.02] text-whisper">{plan.language}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          {plan.review_skills.length > 0 && (
            <div className="space-y-2">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted flex items-center gap-1.5 ml-1">
                <RefreshCw size={10} /> Quick Refresh
              </div>
              <div className="space-y-2">
                {plan.review_skills.map((skill, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-2xl bg-panel/5 border border-panel/10 p-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-aura-blue mt-2 shrink-0" />
                    <div>
                      <div className="font-medium text-ink text-[14.5px] leading-tight mb-1">{skill.title}</div>
                      <div className="text-whisper text-[13px] leading-relaxed">{skill.friendly_reason}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {plan.new_skill && (
            <div className="space-y-2">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted flex items-center gap-1.5 ml-1">
                <Target size={10} /> New Focus
              </div>
              <div className="flex items-start gap-3 rounded-2xl bg-aura-cyan/5 border border-aura-cyan/15 p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-aura-cyan/0 via-aura-cyan/40 to-aura-cyan/0" />
                <div className="w-1.5 h-1.5 rounded-full bg-aura-cyan mt-2 shadow-[0_0_8px_rgba(45,212,191,0.6)] shrink-0" />
                <div>
                  <div className="font-medium text-ink text-[14.5px] leading-tight mb-1">{plan.new_skill.title}</div>
                  <div className="text-whisper text-[13px] leading-relaxed">{plan.new_skill.success_criteria}</div>
                </div>
              </div>
            </div>
          )}

          {plan.repair_note && (
            <div className="flex items-start gap-3 text-[13px] text-aura-violet bg-aura-violet/5 border border-aura-violet/15 rounded-2xl p-4">
              <Sparkles size={16} className="mt-0.5 shrink-0" />
              <span className="leading-relaxed">{plan.repair_note}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
